import { useMutation } from '@tanstack/react-query'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import {
  type Failure,
  RestoreNoTransactionError,
  classifyPurchaseError,
  failureOf,
  createSubscribeFlow,
  type PurchasePresenter,
  type SubscriptionServiceLevelStatus,
  withFailure,
  readOwnerUid,
} from '@forest/paywall-core'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { posthog } from '~/lib/posthog'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import { api } from '~/services/api'
import { native, type Product } from '@forest/native-services'
import { $debugAfterPaymentError } from '~/store/debug'
import { $receiptSystemAccountId } from '~/store/receipt-system-account-id'
import { dialog } from '~/store/dialog'

const flow = createSubscribeFlow<Product>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) =>
      posthog?.captureException(error, context),
  },
  getUserId: async () => (await getAuthenticatedUserInfo()).user_id,
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
  getDebugSimulate: () => $debugAfterPaymentError.get(),
})

const presenter: PurchasePresenter<Product> = {
  success: () => {},
  error: (intent, { error }) => {
    switch (intent.kind) {
      case 'payment_timeout':
        dialog.open({
          title: m.dialog_payment_timeout_title(),
          description: withFailure(
            m.dialog_payment_timeout_content({ error: intent.detail }),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'illegal_transaction_restore':
        dialog.open({
          title: m.dialog_illegal_transaction_title(),
          description: withFailure(
            platform === 'china_android'
              ? m.dialog_illegal_transaction_content_cna()
              : m.dialog_illegal_transaction_content(),
            intent.failure,
          ),
          button: m.restore_purchase(),
          closeButton: true,
          asyncAction: async () => {
            dialog.setLoading(true)
            try {
              await flow.restore()
              dialog.close()
              dialog.open({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                button: m.awesome(),
                action: () => {
                  native.user.refreshServiceLevel()
                  native.general.close()
                },
              })
            } catch (restoreError) {
              dialog.close()
              await showRestoreErrorDialog(restoreError)
            }
          },
        })
        return
      case 'no_internet':
        dialog.open({
          title: m.dialog_no_internet_title(),
          description: withFailure(
            m.dialog_no_internet_content(),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'http_error':
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_http_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      case 'subscription_conflict':
        void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
        return
      case 'unknown':
        posthog?.captureException(error, {
          failure_code: intent.failure.code,
        })
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      default:
        intent satisfies never
    }
  },
}

export function useSubscribeMutation(params?: {
  onSuccess?: (
    serviceLevelStatus: SubscriptionServiceLevelStatus | null,
    variables: Product,
  ) => Promise<unknown> | unknown
}) {
  const { onSuccess } = params ?? {}
  return useMutation({
    mutationFn: (product: Product) => flow.run(product),
    onSuccess: async (serviceLevelStatus, product) => {
      presenter.success(flow.handleSuccess(serviceLevelStatus, product))
      await onSuccess?.(serviceLevelStatus, product)
    },
    onError: (error) => {
      if (error instanceof AbortError) return
      presenter.error(classifyPurchaseError(error), { error, flow })
    },
  })
}

async function showRestoreErrorDialog(error: unknown) {
  const failure = failureOf(error, 'restore')
  if (error instanceof RestoreNoTransactionError) {
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_restore_no_transaction_content({
          app_store_account:
            platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
        }),
        failure,
      ),
      button: m.OK(),
    })
    return
  }

  if (isHTTPError(error)) {
    const status = error.response.status
    if ((status === 409 || status === 412) && platform !== 'china_android') {
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return
    }
    if (status === 403 && platform !== 'china_android') {
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account:
              platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
          }),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    if (
      status === 410 ||
      ((status === 403 || status === 409) && platform === 'china_android')
    ) {
      dialog.open({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_unknown_error_content({ error_code: failure.code }),
        failure,
        { codeInDescription: true },
      ),
      button: m.contact_cs(),
      action: () => {
        openIntercom({
          node: 'restore_purchase_unexpected_http_error',
          orderId: failure.purchaseId?.toString(),
          additional: { error_code: failure.code },
        })
      },
    })
    return
  }

  if (isNetworkError(error) || isTimeoutError(error)) {
    dialog.open({
      title: m.dialog_no_internet_title(),
      description: withFailure(m.dialog_no_internet_content(), failure),
      button: m.OK(),
    })
    return
  }

  dialog.open({
    title: m.dialog_restore_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    button: m.contact_cs(),
    action: () => {
      openIntercom({
        node: 'restore_purchase_unexpected_error',
        orderId: failure.purchaseId?.toString(),
        additional: { error_code: failure.code },
      })
    },
  })
}

/**
 * A subscription conflict: another Forest account owns the subscription. Names
 * that account, or — when it is deleted or unknown — sends the user to manage
 * the store subscription.
 */
async function showSubscriptionConflictDialog(
  ownerUid: number | null,
  failure: Failure,
) {
  const showDeletedAccountDialog = () =>
    dialog.open({
      title: m.dialog_subscription_link_delete_account_title(),
      description: withFailure(
        m.dialog_subscription_link_delete_account_content(),
        failure,
      ),
      button: m.btn_subscription_management(),
      action: () =>
        native.general.openURL(
          'forestapp://instant/system_subscription_management',
        ),
    })
  if (ownerUid === null) {
    showDeletedAccountDialog()
    return
  }
  let userName = ''
  let userEmail = ''
  try {
    const info = await api.service
      .get(`user/users/${ownerUid}/info`)
      .json<{ name: string; email: string }>()
    userName = info.name
    userEmail = info.email
  } catch (e) {
    if (isHTTPError(e) && e.response.status === 404) {
      showDeletedAccountDialog()
      return
    }
  }
  dialog.open({
    title: m.dialog_subscription_conflict_title(),
    description: withFailure(
      m.dialog_subscription_conflict_content({
        user_name: userName,
        user_account: userEmail,
      }),
      failure,
    ),
    button: m.OK(),
  })
}
